import React from 'react';
import TextEdit from '../../../../molecules/TextEdit';
import TextArea from '../../../../molecules/TextArea';
import { TemplateVariable } from '../../../../../types/communication';
import { variableToken } from '../../../../../utils/templateVariables';
import ConstantPicker from './ConstantPicker';
import VariablePicker from './VariablePicker';

interface VariableTextFieldProps {
    id: string;
    label?: string;
    value: string;
    onChange: (value: string) => void;
    variables?: TemplateVariable[];
    hint?: string;
    multiline?: boolean;
    rows?: number;
    disabled?: boolean;
    error?: string;
    maxLength?: number;
}

const VariableTextField: React.FC<VariableTextFieldProps> = ({
    id,
    label = '',
    value,
    onChange,
    variables,
    hint,
    multiline = false,
    rows = 3,
    disabled,
    error,
    maxLength,
}) => {
    const insert = (name: string) => {
        const element = document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
        const token = variableToken(name);
        if (!element) {
            onChange(`${value}${token}`);
            return;
        }
        const start = element.selectionStart ?? value.length;
        const end = element.selectionEnd ?? value.length;
        onChange(`${value.slice(0, start)}${token}${value.slice(end)}`);
        window.requestAnimationFrame(() => {
            element.focus();
            element.setSelectionRange(start + token.length, start + token.length);
        });
    };

    const field = multiline ? (
        <TextArea
            id={id}
            label={label}
            value={value}
            rows={rows}
            disabled={disabled}
            maxLength={maxLength}
            errorMessage={error}
            onChange={(e) => onChange(e.target.value)}
        />
    ) : (
        <TextEdit
            id={id}
            label={label}
            value={value}
            disabled={disabled}
            maxLength={maxLength}
            errorMessage={error}
            helperText={hint}
            onChange={(e) => onChange(e.target.value)}
        />
    );

    return (
        <div>
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">{field}</div>
                {!disabled && variables && (
                    <div className="pt-3 shrink-0 flex items-center gap-1">
                        <VariablePicker variables={variables} onPick={insert} compact />
                        <ConstantPicker onPick={insert} compact />
                    </div>
                )}
            </div>
            {multiline && hint && !error && <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">{hint}</p>}
        </div>
    );
};

export default VariableTextField;
